// SavedPage — onglet "Enregistrés" : lieux favoris + articles à lire plus tard (v5.11.0).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, MapPin, Navigation, Newspaper, Phone, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listSaved, removeSaved } from "@/lib/savedPlaces";
import { listSavedArticles, removeSavedArticle } from "@/lib/savedArticles";

export default function SavedPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("places");
  const [items, setItems] = useState(() => listSaved());
  const [articles, setArticles] = useState(() => listSavedArticles());

  const remove = (id) => {
    removeSaved(id);
    setItems(listSaved());
    toast.success("Retiré des enregistrés");
  };
  const removeArticle = (id) => {
    removeSavedArticle(id);
    setArticles(listSavedArticles());
    toast.success("Article retiré");
  };

  return (
    <div style={P.page} data-testid="saved-page">
      <header style={P.header}>
        <button aria-label="Retour" data-testid="saved-back-btn" onClick={() => navigate("/search")} style={P.backBtn}>
          <ArrowLeft size={19} />
        </button>
        <h1 style={P.h1}><Bookmark size={17} color="#F59E0B" /> Enregistrés</h1>
        <span style={{ color: "#6B7F72", fontSize: 12 }} data-testid="saved-count">{tab === "places" ? items.length : articles.length}</span>
      </header>

      <div style={P.tabsRow} data-testid="saved-tabs">
        <button onClick={() => setTab("places")} style={{ ...P.tabBtn, ...(tab === "places" ? P.tabActive : {}) }} data-testid="saved-tab-places">
          <MapPin size={13} /> Lieux {items.length > 0 && `(${items.length})`}
        </button>
        <button onClick={() => setTab("articles")} style={{ ...P.tabBtn, ...(tab === "articles" ? P.tabActive : {}) }} data-testid="saved-tab-articles">
          <Newspaper size={13} /> Articles {articles.length > 0 && `(${articles.length})`}
        </button>
      </div>

      {tab === "articles" ? (
        articles.length === 0 ? (
          <div style={P.empty} data-testid="saved-articles-empty">
            <Newspaper size={34} color="#34D399" />
            <p style={{ color: "#CFE5D6", fontSize: 13.5, margin: "10px 0 4px", fontWeight: 700 }}>Aucun article enregistré</p>
            <p style={{ color: "#9DB4A6", fontSize: 12, margin: "0 0 14px" }}>
              Touchez « Enregistrer » sous une actualité pour la lire plus tard.
            </p>
            <button onClick={() => navigate("/search")} style={P.cta} data-testid="saved-go-news">
              <Newspaper size={14} /> Voir les actualités
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 14, padding: "4px 14px 24px" }}>
            {articles.map((s) => (
              <article key={s.id} style={P.artRow} data-testid={`saved-article-${s.id}`}>
                <a href={s.url} target="_blank" rel="noreferrer" style={P.artLink} data-testid={`saved-article-open-${s.id}`}>
                  {s.image ? (
                    <img src={s.image} alt="" style={P.artThumb} loading="lazy" referrerPolicy="no-referrer"
                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  ) : (
                    <div style={{ ...P.artThumb, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(16,185,129,0.1)" }}>
                      <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: 2, color: "rgba(52,211,153,0.55)" }}>AYA</span>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {s.tag && <span style={P.artTag}>{s.flag} {s.tag}</span>}
                    <h3 style={P.artTitle}>{s.title}</h3>
                    <div style={{ color: "#6B7F72", fontSize: 11 }}>
                      {s.source}{s.age_label ? ` · ${s.age_label}` : ""}
                    </div>
                  </div>
                </a>
                <button onClick={() => removeArticle(s.id)} style={P.artRemove} data-testid={`saved-article-remove-${s.id}`} aria-label="Retirer l'article">
                  <Trash2 size={14} />
                </button>
              </article>
            ))}
          </div>
        )
      ) : items.length === 0 ? (
        <div style={P.empty} data-testid="saved-empty">
          <Bookmark size={34} color="#34D399" />
          <p style={{ color: "#CFE5D6", fontSize: 13.5, margin: "10px 0 4px", fontWeight: 700 }}>Aucun favori pour le moment</p>
          <p style={{ color: "#9DB4A6", fontSize: 12, margin: "0 0 14px" }}>
            Touchez le marque-page sur une fiche de recherche pour la retrouver ici.
          </p>
          <button onClick={() => navigate("/search")} style={P.cta} data-testid="saved-go-search">
            <Search size={14} /> Lancer une recherche
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, padding: "0 14px 24px" }}>
          {items.map((s) => (
            <article key={s.id} style={P.card} data-testid={`saved-item-${s.id}`}>
              {s.image && <img src={s.image} alt="" style={P.img} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              <div style={{ padding: "10px 12px 12px", position: "relative" }}>
                {s.is_mock && <span style={P.testBadge}>TEST</span>}
                {s.category_label && <span style={P.catTag}>{s.category_label}</span>}
                <h3 style={P.name}>{s.name}</h3>
                {s.commune_name && (
                  <div style={{ color: "#9DB4A6", fontSize: 11.5, display: "flex", alignItems: "center", gap: 4 }}>
                    <MapPin size={11} /> {s.commune_name}
                  </div>
                )}
                <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
                  {s.link && (
                    <button onClick={() => navigate(s.link)} style={{ ...P.btn, color: "#34D399", fontWeight: 800 }} data-testid={`saved-open-${s.id}`}>Voir</button>
                  )}
                  {s.tel && (
                    <button onClick={() => { window.location.href = `tel:${String(s.tel).replace(/\s/g, "")}`; }} style={P.btn} data-testid={`saved-call-${s.id}`}>
                      <Phone size={12} /> Appeler
                    </button>
                  )}
                  {s.lat != null && s.lng != null && (
                    <button onClick={() => navigate(`/map-explorer?lat=${s.lat}&lng=${s.lng}&label=${encodeURIComponent(s.name)}`)} style={{ ...P.btn, color: "#93C5FD" }} data-testid={`saved-route-${s.id}`}>
                      <Navigation size={12} /> Itinéraire
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} style={{ ...P.btn, color: "#F87171", borderColor: "rgba(239,68,68,0.4)", marginLeft: "auto" }} data-testid={`saved-remove-${s.id}`}>
                    <Trash2 size={12} /> Retirer
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const P = {
  page: { minHeight: "100vh", background: "#04120B", color: "#ECF5EE", fontFamily: "'Manrope', system-ui, sans-serif" },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "14px 14px 12px", position: "sticky", top: 0, background: "rgba(4,18,11,0.95)", backdropFilter: "blur(8px)", zIndex: 10 },
  backBtn: { width: 36, height: 36, borderRadius: 12, border: "1px solid rgba(16,185,129,0.3)", background: "rgba(8,38,24,0.7)", color: "#CFE5D6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  h1: { display: "flex", alignItems: "center", gap: 7, fontSize: 17, fontWeight: 800, margin: 0, flex: 1 },
  tabsRow: { display: "flex", gap: 8, padding: "2px 14px 12px" },
  tabBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 15px", borderRadius: 999, border: "1px solid rgba(16,185,129,0.3)", background: "rgba(8,38,24,0.6)", color: "#9DB4A6", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  tabActive: { background: "rgba(16,185,129,0.18)", color: "#34D399", borderColor: "#10B981" },
  artRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  artLink: { display: "flex", gap: 11, flex: 1, minWidth: 0, textDecoration: "none", cursor: "pointer" },
  artThumb: { width: 96, height: 66, objectFit: "cover", borderRadius: 11, flexShrink: 0, background: "#06281A" },
  artTag: { display: "inline-block", fontSize: 9, fontWeight: 800, letterSpacing: 1.1, color: "#34D399", textTransform: "uppercase", marginBottom: 2 },
  artTitle: { margin: "0 0 3px", fontSize: 13.5, lineHeight: 1.32, fontWeight: 800, color: "#ECF5EE", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  artRemove: { width: 32, height: 32, borderRadius: 9, border: "1px solid rgba(239,68,68,0.35)", background: "transparent", color: "#F87171", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  empty: { textAlign: "center", padding: "70px 24px" },
  cta: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  card: { borderRadius: 15, overflow: "hidden", background: "rgba(8,38,24,0.75)", border: "1px solid rgba(16,185,129,0.22)" },
  img: { width: "100%", height: 110, objectFit: "cover", display: "block" },
  testBadge: { position: "absolute", top: 8, right: 10, padding: "2px 9px", borderRadius: 999, background: "#F59E0B", color: "#1A1206", fontSize: 9, fontWeight: 900, letterSpacing: 1 },
  catTag: { display: "inline-block", padding: "2px 8px", borderRadius: 999, background: "rgba(16,185,129,0.15)", color: "#6EE7B7", fontSize: 9, fontWeight: 800, letterSpacing: 0.5 },
  name: { margin: "5px 0 3px", fontSize: 14.5, fontWeight: 800, color: "#ECF5EE" },
  btn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 11px", borderRadius: 9, border: "1px solid rgba(16,185,129,0.35)", background: "transparent", color: "#CFE5D6", fontSize: 11.5, cursor: "pointer" },
};
